import { Box, Typography, TextField, Autocomplete } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import * as React from 'react';
import { useState, useCallback, useEffect } from 'react';
import { Vehicle, VEHICLE_FIELDS, Choice } from 'types/vehicles/types';
import { vehiclePageStyles } from '../styles';
import {
  MECHANICAL_STATUS_LABEL_COLOR_MAP,
  MECHANICAL_STATUS_UNKNOWN,
  MECHANICAL_STATUS_LABEL_MAP,
} from '../../config';
import axios from 'axios';
import axiosService from 'utils/axios';
import '../styles.css';

export interface VehicleDetailsSectionData {
  vin: string;
  name: string;
  fuel_type: string;
  technical_status: string | number;
  weight: string;
  keys: number | string;
}

interface VehicleDetailsSectionProps {
  updateSubmitData: (data: VehicleDetailsSectionData) => void;
  setIsLoading: (loading: boolean) => void;
  canView: (fieldName: string) => boolean;
  canEdit: (fieldName: string) => boolean;
  readOnly?: boolean;
}

export interface VehicleDetailsSectionRef {
  updateSectionData: (vehicleData: Vehicle) => void;
}

const VehicleDetailsSection = React.forwardRef(
  ({ updateSubmitData, setIsLoading, canView, canEdit, readOnly }: VehicleDetailsSectionProps, ref) => {
    const newEntryMechanicalStatus = MECHANICAL_STATUS_UNKNOWN;
    const newEntryMechanicalStatusLabel = MECHANICAL_STATUS_LABEL_MAP[newEntryMechanicalStatus];

    // Technical status options
    const [technicalStatusOptions, setTechnicalStatusOptions] = useState<Choice[]>([]);
    const [isTechnicalOptionsLoading, setIsTechnicalOptionsLoading] = useState(false);

    // Vehicle details states
    const [vinInput, setVinInput] = useState<string>('');
    const [vinDecodeLoading, setVinDecodeLoading] = useState(false);
    const [vinDecodeError, setVinDecodeError] = useState<string>('');
    const [nameInput, setNameInput] = useState<string>('');
    const [fuelTypeInput, setFuelTypeInput] = useState<string>('');
    const [weightInput, setWeightInput] = useState<string>('');
    const [keysInput, setKeysInput] = useState<number | string>(0);
    const [technicalStatusChoice, setTechnicalStatusChoice] = useState<Choice>({
      value: newEntryMechanicalStatus,
      // eslint-disable-next-line camelcase
      display_name: newEntryMechanicalStatusLabel,
    });

    const getTechnicalStatusOptions = useCallback(() => {
      setIsTechnicalOptionsLoading(true);
      axiosService
        .options(`/vehicles/vehicle/`)
        .then(res => {
          setTechnicalStatusOptions(res.data.actions.POST?.technical_status?.choices || []);
        })
        .finally(() => setIsTechnicalOptionsLoading(false));
    }, []);

    const decodeVin = useCallback(() => {
      if (!vinInput) return;
      setIsLoading(true);
      setVinDecodeLoading(true);
      setVinDecodeError('');
      const decodeVinURL = `https://vpic.nhtsa.dot.gov/api/vehicles/decodevinvalues/${vinInput}?format=json`;
      axios
        .get(decodeVinURL)
        .then(({ data }) => {
          const result = data?.Results?.[0];
          if (!result) {
            setVinDecodeError('Unable to decode VIN. Please try again.');
            return;
          }

          const hasUsableData = Boolean(result.Make || result.Model || result.ModelYear);
          if (!hasUsableData) {
            setVinDecodeError(result.ErrorText || 'Unable to decode VIN.');
            return;
          }

          const nameInputValue = [result.ModelYear, result.Make, result.Model, result.Trim]
            .filter(Boolean)
            .join(' ')
            .trim();
          setNameInput(nameInputValue);

          const fuelTypeInputValue = [result.FuelTypePrimary, result.FuelTypeSecondary]
            .filter(Boolean)
            .join(' ')
            .trim();
          setFuelTypeInput(fuelTypeInputValue);

          try {
            if (result.GVWR && result.GVWR.includes('(')) {
              setWeightInput(`${result.GVWR.split('(')[1]}`.slice(0, -1).trim());
            }
          } catch (e) {}
        })
        .catch(e => setVinDecodeError(`${e}`))
        .finally(() => {
          setIsLoading(false);
          setVinDecodeLoading(false);
        });
    }, [vinInput, setIsLoading]);

    useEffect(() => {
      if (!readOnly) {
        getTechnicalStatusOptions();
      }
    }, [getTechnicalStatusOptions, readOnly]);

    React.useEffect(() => {
      // When one of the internal states change, update the submit data
      updateSubmitData({
        vin: vinInput,
        name: nameInput,
        // eslint-disable-next-line camelcase
        fuel_type: fuelTypeInput,
        // eslint-disable-next-line camelcase
        technical_status: technicalStatusChoice?.value,
        weight: weightInput,
        keys: keysInput,
      });
    }, [vinInput, nameInput, fuelTypeInput, technicalStatusChoice?.value, weightInput, keysInput, updateSubmitData]);

    React.useImperativeHandle(ref, () => ({
      updateSectionData: (vehicleData: Vehicle) => {
        setVinInput(vehicleData?.vin);
        setNameInput(vehicleData?.name);
        setFuelTypeInput(vehicleData?.fuel_type);
        setWeightInput(vehicleData?.weight);
        setKeysInput(vehicleData?.keys || 0);
        setTechnicalStatusChoice(
          vehicleData?.technical_status || {
            value: newEntryMechanicalStatus,
            // eslint-disable-next-line camelcase
            display_name: newEntryMechanicalStatusLabel,
          }
        );
      },
    }));

    return (
      <Box sx={vehiclePageStyles.card}>
        <Box sx={vehiclePageStyles.cardHeader}>Vehicle Details</Box>
        {canView(VEHICLE_FIELDS.VIN) ? (
          <Box sx={vehiclePageStyles.detailBox}>
            <Typography sx={vehiclePageStyles.detailTitle}>VIN{canEdit(VEHICLE_FIELDS.VIN) ? ' *' : ''}</Typography>
            {canEdit(VEHICLE_FIELDS.VIN) ? (
              <Box
                sx={{
                  display: 'flex',
                  gap: '10px',
                  width: { xs: '75%', sm: '60%' },
                }}
              >
                <TextField
                  required
                  fullWidth
                  variant="standard"
                  size="small"
                  id="vin"
                  value={vinInput}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    setVinInput(event.target.value);
                  }}
                  inputProps={{
                    sx: {
                      ...vehiclePageStyles.detailTextFieldInput,
                    },
                  }}
                />
                <LoadingButton
                  loading={vinDecodeLoading}
                  onClick={decodeVin}
                  variant="contained"
                  disabled={!vinInput}
                  size="small"
                >
                  Decode
                </LoadingButton>
              </Box>
            ) : (
              <Box sx={vehiclePageStyles.detailValue}>{vinInput}</Box>
            )}
          </Box>
        ) : null}
        {vinDecodeError ? (
          <Typography sx={{ textAlign: 'right' }} color="error">
            {vinDecodeError}
          </Typography>
        ) : null}
        {canView(VEHICLE_FIELDS.NAME) ? (
          <Box sx={vehiclePageStyles.detailBox}>
            <Typography sx={vehiclePageStyles.detailTitle}>
              Vehicle{canEdit(VEHICLE_FIELDS.NAME) ? ' *' : ''}
            </Typography>
            {canEdit(VEHICLE_FIELDS.NAME) ? (
              <TextField
                required
                variant="standard"
                size="small"
                id="name"
                value={nameInput}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setNameInput(event.target.value);
                }}
                sx={vehiclePageStyles.detailTextField}
                inputProps={{
                  sx: {
                    ...vehiclePageStyles.detailTextFieldInput,
                  },
                }}
              />
            ) : (
              <Box sx={vehiclePageStyles.detailValue}>{nameInput}</Box>
            )}
          </Box>
        ) : null}
        {canView(VEHICLE_FIELDS.FUEL_TYPE) ? (
          <Box sx={vehiclePageStyles.detailBox}>
            <Typography sx={vehiclePageStyles.detailTitle}>Fuel Type</Typography>
            {canEdit(VEHICLE_FIELDS.FUEL_TYPE) ? (
              <TextField
                variant="standard"
                size="small"
                id="fuel_type"
                value={fuelTypeInput}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setFuelTypeInput(event.target.value);
                }}
                sx={vehiclePageStyles.detailTextField}
                inputProps={{
                  sx: {
                    ...vehiclePageStyles.detailTextFieldInput,
                  },
                }}
              />
            ) : (
              <Box sx={vehiclePageStyles.detailValue}>{fuelTypeInput || '-'}</Box>
            )}
          </Box>
        ) : null}
        {canView(VEHICLE_FIELDS.MECHANICAL_STATUS) ? (
          <Box sx={vehiclePageStyles.detailBox}>
            <Typography sx={vehiclePageStyles.detailTitle}>Mechanical Status</Typography>
            {canEdit(VEHICLE_FIELDS.MECHANICAL_STATUS) ? (
              <Autocomplete
                disableClearable
                value={technicalStatusChoice}
                onChange={(event, value) => setTechnicalStatusChoice(value)}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                getOptionLabel={option => option.display_name}
                options={technicalStatusOptions}
                loading={isTechnicalOptionsLoading}
                sx={vehiclePageStyles.detailTextField}
                renderInput={params => (
                  <TextField
                    {...params}
                    variant="standard"
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      sx: {
                        ...vehiclePageStyles.detailTextFieldInput,
                        color:
                          technicalStatusChoice?.value !== undefined
                            ? MECHANICAL_STATUS_LABEL_COLOR_MAP[technicalStatusChoice.value]
                            : 'inherit',
                      },
                    }}
                  />
                )}
              />
            ) : (
              <Box
                sx={{
                  ...vehiclePageStyles.detailValue,
                  color:
                    technicalStatusChoice?.value !== undefined
                      ? MECHANICAL_STATUS_LABEL_COLOR_MAP[technicalStatusChoice.value]
                      : 'inherit',
                }}
              >
                {technicalStatusChoice?.display_name || '-'}
              </Box>
            )}
          </Box>
        ) : null}
        {canView(VEHICLE_FIELDS.WEIGHT) ? (
          <Box sx={vehiclePageStyles.detailBox}>
            <Typography sx={vehiclePageStyles.detailTitle}>Vehicle Weight</Typography>
            {canEdit(VEHICLE_FIELDS.WEIGHT) ? (
              <TextField
                variant="standard"
                size="small"
                id="weight"
                value={weightInput}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setWeightInput(event.target.value);
                }}
                sx={vehiclePageStyles.detailTextField}
                inputProps={{
                  sx: {
                    ...vehiclePageStyles.detailTextFieldInput,
                  },
                }}
              />
            ) : (
              <Box sx={vehiclePageStyles.detailValue}>{weightInput || '-'}</Box>
            )}
          </Box>
        ) : null}
        {canView(VEHICLE_FIELDS.KEYS) ? (
          <Box sx={vehiclePageStyles.detailBox}>
            <Typography sx={vehiclePageStyles.detailTitle}>Keys</Typography>
            {canEdit(VEHICLE_FIELDS.KEYS) ? (
              <TextField
                variant="standard"
                size="small"
                id="keys"
                value={keysInput}
                type="number"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  if (event.target.value) {
                    setKeysInput(parseFloat(event.target.value));
                  } else {
                    setKeysInput('');
                  }
                }}
                sx={vehiclePageStyles.detailTextField}
                inputProps={{
                  min: 0,
                  sx: {
                    ...vehiclePageStyles.detailTextFieldInput,
                  },
                }}
              />
            ) : (
              <Box sx={vehiclePageStyles.detailValue}>{keysInput || '-'}</Box>
            )}
          </Box>
        ) : null}
      </Box>
    );
  }
);

VehicleDetailsSection.displayName = 'VehicleDetailsSection';

export default VehicleDetailsSection;
